import constants from "../constants";
import {
  get,
  getQueryString,
  getResponseAsBlob,
  getQueryNonEmptyString,
  post,
  put,
  deleteItem,
  uploadDoc,
  patch
} from "../utils";
import { UploadFreightClaimAttachmentFile } from "src/pages/CreateFreightClaim/interface";
import {
  DailySalesReportRequestParams,
  DeptExceptionRequestParams,
  PriceExceptionRequestParams
} from "src/config/tableInterface/interface";
import { ProductsShowAllTableRequestParams } from "../../pages/SearchShowAllProducts/interface";
import { ReceivingShowAllRequestParams } from "../../pages/SearchShowAllReceivings/interface";
import { PurchaseReportsRequestParams } from "src/pages/PurchaseRegister/interface";
import { getFormattedDate } from "../../utils/getDates";

export const getPriceChangesData = ({
  department,
  storeId,
  startDate,
  endDate
}: {
  department: string;
  storeId: string;
  startDate: string;
  endDate: string;
}) => {
  const URL = constants.DOT_NET_API_BASE_URL + "v1/sp/DSDPriceChanges";

  const payload = {
    storeId,
    department,
    currentDate: getFormattedDate(new Date()),
    fromDate: startDate,
    toDate: endDate
  };

  return post(URL, payload, true, true);
};

export const postPriceChangesData = ({
  department,
  storeId,
  row,
  currentDate,
  createdBy
}: {
  department: string;
  storeId: string;
  row: any;
  currentDate: string;
  createdBy: string;
}) => {
  const URL = constants.DOT_NET_API_BASE_URL + "v1/sp/dsdpricechanges/snooze";

  const payload = {
    storeId,
    itemId: row.itemId,
    snoozedDate: currentDate,
    isSnoozed: !row.isSnoozed,
    department,
    createdBy
  };

  return post(URL, payload, true, true);
};

export const getUser = ({ email }: { email: string; idToken?: string }) => {
  const queryString = getQueryString({ email: encodeURIComponent(email) });

  const URL =
    constants.DOT_NET_API_BASE_URL +
    `v1/sp/storeportal/users/getUser?${queryString}`;
  return get(URL, true, true);
};

export const updateStore = (payload: object) => {
  const URL = constants.DOT_NET_API_BASE_URL + "v1/sp/users/updateStore";
  return post(URL, payload, true, true);
};

export const getItemAlertsData = ({
  department,
  storeId,
  countDate
}: {
  department: string;
  storeId: string;
  countDate: string;
}) => {
  const queryString = getQueryString({ department, storeId, countDate });
  const URL =
    constants.DOT_NET_API_BASE_URL + `v1/sp/itemAlerts?${queryString}`;
  return get(URL, true, true);
};

export const getPriceExceptionData = (payload: PriceExceptionRequestParams) => {
  const URL = constants.DOT_NET_API_BASE_URL + "v1/sp/PriceException";
  return post(URL, payload, true, true, false);
};

export const getDeptExceptionData = (payload: DeptExceptionRequestParams) => {
  const URL = constants.DOT_NET_API_BASE_URL + "v1/sp/DepartmentException";
  return post(URL, payload, true, true, false);
};

export const getReminderTileData = ({
  departmentId,
  storeId,
  createdDate
}: {
  departmentId: string;
  storeId: string;
  createdDate: string;
}) => {
  const queryString = getQueryString({ departmentId, storeId, createdDate });

  const URL =
    constants.DOT_NET_API_BASE_URL + `v1/sp/reminders/list?${queryString}`;

  return get(URL, true, true);
};

export const postNewReminder = ({ payload }: { payload: object }) => {
  const URL = constants.DOT_NET_API_BASE_URL + "v1/sp/reminders/add";

  return post(URL, payload, true, true);
};

export const deleteSelectedReminder = ({
  reminderId,
  parentId
}: {
  reminderId: string;
  parentId: string;
}) => {
  const queryString = getQueryString({ reminderId, parentId });

  const URL =
    constants.DOT_NET_API_BASE_URL + `v1/sp/reminders/delete?${queryString}`;
  return post(URL, {}, true, true);
};

export const getDefaultView = (storeId: string) => {
  const URL =
    constants.DOT_NET_API_BASE_URL + `v1/sp/storepreferences/${storeId}/tiles`;

  return get(URL, true, true);
};

export const getTileList = (storeId: string) => {
  const URL =
    constants.DOT_NET_API_BASE_URL +
    `v1/sp/storepreferences/dashboardstore/${storeId}`;

  return get(URL, true, true);
};

export const getTransferPOProducts = ({
  receivingDocId,
  storeId
}: {
  receivingDocId: number;
  storeId: string;
}) => {
  const URL =
    constants.DOT_NET_API_BASE_URL +
    `v1/sp/receivings/details?docId=${receivingDocId}&type=Transfer PO&storeId=${storeId}`;

  return get(URL, true, true);
};

export const getStoreCreditDetails = ({
  storeId,
  referenceTransfer,
  type
}: {
  storeId: string;
  referenceTransfer: number;
  type: string;
}) => {
  const URL =
    constants.DOT_NET_API_BASE_URL +
    `v1/sp/receivings/storeCreditdetails?referenceTransfer=${referenceTransfer}&type=${type}&storeId=${storeId}`;
  return get(URL, true, true);
};

export const getFreighClaimProducts = ({
  receivingDocId,
  referenceId,
  storeId
}: {
  receivingDocId: number;
  referenceId: string;
  storeId: string;
}) => {
  const URL =
    constants.DOT_NET_API_BASE_URL +
    `v1/sp/receivings/details?docId=${receivingDocId}&internalReferenceId=${referenceId}&type=Freight%20Claim&storeId=${storeId}`;

  return get(URL, true, true);
};

export const postTileUpdate = (payload: object) => {
  const URL =
    constants.DOT_NET_API_BASE_URL +
    "v1/sp/storepreferences/customizedtiles/update";
  return post(URL, payload, true, true);
};

export const getGlobalAlertsData = ({
  storeId
}: {
  storeId: string;
  type?: string;
}) => {
  const URL =
    constants.DOT_NET_API_BASE_URL + `v1/sp/Alert/${storeId}?type=all`;

  return get(URL, true, true);
};

export const postGlobalAlertsSnoozeData = ({
  storeId,
  alertId
}: {
  storeId: string;
  alertId: string;
}) => {
  const URL =
    constants.DOT_NET_API_BASE_URL +
    `v1/sp/Alert/${storeId}?alertId=${alertId}`;
  return put(URL, {}, true, true);
};

export const postUserDetailsData = (payload: object) => {
  const URL =
    constants.DOT_NET_API_BASE_URL + "v1/sp/storeportal/users/updateUser";

  return post(URL, payload, true, true);
};

export const postManualTransferPO = (payload: object) => {
  const URL =
    constants.DOT_NET_API_BASE_URL + "v1/sp/receivings/manualtransferpo";
  return post(URL, payload, true, true);
};

export const getProductPricing = ({
  storeId,
  itemId,
  signal
}: {
  storeId: string;
  itemId: string | number;
  signal?: AbortSignal;
}) => {
  const queryString = getQueryString({
    storeId: String(storeId),
    itemId: String(itemId)
  });
  const URL =
    constants.DOT_NET_API_BASE_URL + `v1/sp/product/pricing?${queryString}`;
  return get(URL, true, true, true, signal);
};

export const postPoCreate = (payload: object) => {
  const URL = constants.DOT_NET_API_BASE_URL + "v1/sp/receivings/storecredit";

  return post(URL, payload, true, true);
};

export const getPerpInventoryAvgReportData = ({
  storeId,
  department
}: {
  storeId: string;
  department: string;
}) => {
  const payload = {
    storeId,
    department
  };

  const URL =
    constants.DOT_NET_API_BASE_URL +
    "v1/sp/reports/perpetualinventoryaveragesreport";

  return post(URL, payload, true, true);
};

export const uploadFreightClaimAttachmentsCall = (
  payload: UploadFreightClaimAttachmentFile
) => {
  const { receivingId, invoiceType, reqBody } = payload;
  const URL =
    constants.DOT_NET_API_BASE_URL +
    `v1/sp/receivings/attachment/upload?receivingId=${receivingId}&invoiceType=${invoiceType}`;

  return uploadDoc(URL, reqBody, true, true);
};

export const deleteFreightClaimAttachmentsCall = (filePath: string) => {
  const URL =
    constants.DOT_NET_API_BASE_URL +
    `v1/sp/receivings/attachment/delete?filePath=${filePath}`;

  return deleteItem(URL, true, true);
};

export const getSearchResults = async ({
  searchTerm,
  storeId,
  signal
}: {
  searchTerm: string;
  storeId: string;
  signal: AbortSignal;
}) => {
  const URL =
    constants.GLOBAL_SEARCH_BASE_URL +
    `v1/search/globalsearch?take=3&searchTerm=${searchTerm}&storeId=${storeId}`;

  return get(URL, true, true, false, signal);
};

export const getStoreCreditStatus = async ({ docId }: { docId: string }) => {
  const URL =
    constants.DOT_NET_API_BASE_URL +
    `v1/sp/receivings/storecredit/exists?docId=${docId}`;
  return get(URL, true, true);
};

export const getPoDetails = ({
  docId,
  storeId
}: {
  docId: string;
  storeId: string;
}) => {
  const URL =
    constants.DOT_NET_API_BASE_URL +
    `v1/sp/receivings/details?docId=${docId}&type=Transfer%20PO&storeId=${storeId}`;

  return get(URL, true, true);
};

export const getPerpInventoryData = ({
  department,
  storeId
}: {
  department: string;
  storeId: string;
}) => {
  const queryObj = {
    department,
    storeId
  };

  if (queryObj.department === "all") delete queryObj.department;

  const queryString = getQueryString(queryObj);

  const URL =
    constants.DOT_NET_API_BASE_URL +
    "v1/sp/reports/perpetualinventoryaveragesreportSummary" +
    `?${queryString}`;

  return get(URL, true, true);
};

export const getReportsListingData = ({
  userEmail,
  storeId
}: {
  userEmail: string;
  storeId: string;
}) => {
  const queryString = getQueryString({
    userEmail: encodeURIComponent(userEmail),
    storeId
  });

  const URL =
    constants.DOT_NET_API_BASE_URL + `v1/sp/reports/list?${queryString}`;
  return get(URL, true, true);
};

export const postFavoriteStatus = ({ payload }: { payload: object }) => {
  const URL = constants.DOT_NET_API_BASE_URL + "v1/sp/reports/markfavourite";
  return post(URL, payload, true, true);
};

export const getReceiveDetails = (
  referenceId: any,
  type: any,
  docId: any,
  storeId: any,
  sendDate?: string
) => {
  const URL =
    constants.DOT_NET_API_BASE_URL +
    `v1/sp/receivings/details?docId=${docId}&internalReferenceId=${referenceId}&type=${type}&storeId=${storeId}&sendDate=${sendDate}`;

  return get(URL, true, true);
};

export const downloadAttachments = (
  filePath: string,
  fileName: string,
  boxId?: string
) => {
  const URL = boxId
    ? constants.DOT_NET_API_BASE_URL +
    `v1/sp/receivings/box/attachment/download?boxId=${boxId}&fileName=${fileName}`
    : constants.DOT_NET_API_BASE_URL +
    `v1/sp/receivings/attachment/download?filePath=${filePath}&fileName=${fileName}`;
  return getResponseAsBlob(URL, true, true);
};

export const getFreightClaimEditableStatus = async ({
  docId,
  currentDate
}: {
  docId: string;
  currentDate: string;
}) => {
  const URL =
    constants.DOT_NET_API_BASE_URL +
    `v1/sp/receivings/iseditable?docId=${docId}&currentDate=${currentDate}`;
  return get(URL, true, true);
};

export const postInventoryAdjustment = (payload: any) => {
  const URL =
    constants.DOT_NET_API_BASE_URL + "v1/sp/receivings/inventoryadjustment";

  return post(URL, payload, true, true);
};

export const getPhysicalCreditTable = (props: any) => {
  const URL =
    constants.DOT_NET_API_BASE_URL +
    `v1/sp/receivings/AutoCreateDMR?storeId=${props.storeId}`;
  return get(URL, true, true);
};

export const postPhysicalInventory = (payload: any) => {
  const URL =
    constants.DOT_NET_API_BASE_URL + "v1/sp/receivings/physicalinventory";

  return post(URL, payload, true, true);
};

export const postPhysicalInventoryIAU = (payload: any) => {
  const URL =
    constants.DOT_NET_API_BASE_URL +
    "v1/sp/receivings/beginningphysicalinventory";

  return post(URL, payload, true, true, true);
};

export const postUpdateWarehouseTransfer = (payload: any) => {
  const URL =
    constants.DOT_NET_API_BASE_URL + "v1/sp/receivings/warehousetransfer";

  return post(URL, payload, true, true);
};

export const postUpdateDSDData = (payload: any) => {
  const URL = constants.DOT_NET_API_BASE_URL + "v1/sp/receivings/dsd";

  return post(URL, payload, true, true);
};

export const postUpdatePerpInvAvgGoal = (payload: any) => {
  const URL = constants.DOT_NET_API_BASE_URL + "v1/sp/reports/updategoal";

  return post(URL, payload, true, true);
};

export const postExportReport = (payload: any, showErrorToast = true) => {
  const URL = constants.DOT_NET_API_BASE_URL + "v1/sp/reports/exportreport";

  return post(URL, payload, true, true, showErrorToast);
};

export const getOverShortDetails = (
  internalReferenceId: string,
  storeId: string
) => {
  const URL =
    constants.DOT_NET_API_BASE_URL +
    `v1/sp/receivings/details?type=Over/Short&internalReferenceId=${internalReferenceId}&storeId=${storeId}`;

  return get(URL, true, true);
};

export const getGeneratedReports = (payload: any) => {
  const URL = constants.DOT_NET_API_BASE_URL + "v1/sp/generatedreports/list";

  return post(URL, payload, true, true, false);
};

export const regenerateReport = (generatedReportId: string) => {
  const URL =
    constants.DOT_NET_API_BASE_URL +
    `v1/sp/generatedreports/regenerate?generatedReportId=${generatedReportId}`;

  return post(URL, {}, true, true);
};

export const deleteReport = (generatedReportId: string) => {
  const URL =
    constants.DOT_NET_API_BASE_URL +
    `v1/sp/generatedreports/Delete?generatedReportId=${generatedReportId}`;

  return post(URL, {}, true, true);
};

export const getReportTypes = () => {
  const URL =
    constants.DOT_NET_API_BASE_URL + "v1/sp/generatedreports/reporttype";

  return get(URL, true, true);
};

export const getPdfReport = ({
  filePath,
  fileName
}: {
  filePath?: string;
  fileName?: string;
}) => {
  const _queryString = getQueryString({ filePath, fileName });

  const URL =
    constants.DOT_NET_API_BASE_URL +
    "v1/sp/receivings/attachment/download?" +
    _queryString;

  return getResponseAsBlob(URL, true, true);
};

export const updateReportStatus = (generatedReportId: string) => {
  const URL =
    constants.DOT_NET_API_BASE_URL + "v1/sp/generatedreports/updatestatus";

  return post(URL, { generatedReportId }, true, true);
};

export const postCreateYellowAdjustment = (payload: any) => {
  const URL =
    constants.DOT_NET_API_BASE_URL + "v1/sp/receivings/yellowadjustment";

  return post(URL, payload, true, true, true);
};

export const getYellowTransferDetails = ({ id, storeId, docId }: any) => {
  const URL =
    constants.DOT_NET_API_BASE_URL +
    `v1/sp/receivings/details?internalReferenceId=${id}&type=Yellow&storeId=${storeId}&docId=${docId}`;

  return get(URL, true, true);
};

export const uploadYellowAttachmentsCall = (payload: any) => {
  const { receivingId, reqBody } = payload;
  const URL =
    constants.DOT_NET_API_BASE_URL +
    `v1/sp/receivings/attachment/upload?receivingId=${receivingId}&invoiceType=Yellow`;

  return uploadDoc(URL, reqBody, true, true);
};

export const deleteYellowAttachmentsCall = (path: string) => {
  const URL =
    constants.DOT_NET_API_BASE_URL +
    `v1/sp/receivings/attachment/delete?filePath=${path}`;

  return deleteItem(URL, true, true);
};

export const uploadAttachment = (payload: any) => {
  const { receivingId, receivingType, reqBody } = payload;
  const URL =
    constants.DOT_NET_API_BASE_URL +
    `v1/sp/receivings/attachment/upload?receivingId=${receivingId}&invoiceType=${receivingType}`;

  return uploadDoc(URL, reqBody, true, true);
};

export const deleteAttachment = (fileUrl: any) => {
  const URL =
    constants.DOT_NET_API_BASE_URL +
    "v1/sp/receivings/attachment/delete?filePath=" +
    fileUrl;

  return deleteItem(URL, true, true);
};

export const postApproveOrRejectStoreCredit = (payload: any) => {
  const URL =
    constants.DOT_NET_API_BASE_URL + "v1/sp/receivings/storecredit/approval";

  return post(URL, payload, true, true);
};

export const postApproveOrRejectInventoryAdjustment = (payload: any) => {
  const URL =
    constants.DOT_NET_API_BASE_URL +
    "v1/sp/receivings/inventoryadjustment/approval";

  return post(URL, payload, true, true);
};

export const postApproveRejectReconcilePhysicalInventory = (payload: any) => {
  const URL =
    constants.DOT_NET_API_BASE_URL +
    "v1/sp/receivings/physicalinventory/approval";

  return post(URL, payload, true, true);
};

export const getDailySalesReportData = (
  paramObj: DailySalesReportRequestParams
) => {
  const signal = paramObj.signal;
  delete paramObj.signal;
  const queryString = getQueryNonEmptyString(paramObj);
  const URL =
    constants.DOT_NET_API_BASE_URL + `v1/sp/reports/dailySales?${queryString}`;
  return get(URL, true, true, false, signal);
};

export const getGrossMarginReport = (payload: any) => {
  const { signal, fromDate, toDate, storeId } = payload;
  const reqPayload = { fromDate, toDate, storeId };

  const queryString = getQueryString(reqPayload);
  const URL =
    constants.DOT_NET_API_BASE_URL + `v1/sp/reports/grossmargin?${queryString}`;
  return get(URL, true, true, false, signal);
};

export const getConfiguration = () => {
  const URL = constants.DOT_NET_API_BASE_URL + "v1/sp/configurations";
  return get(URL, true, true);
};

export const getSearchProductsShowAllData = (
  paramObj: ProductsShowAllTableRequestParams
) => {
  const URL =
    constants.GLOBAL_SEARCH_BASE_URL + "v1/search/globalsearchproductdetails";
  return post(URL, paramObj, true, true, false);
};
export const getSearchReceivingsShowAllData = (
  paramObj: ReceivingShowAllRequestParams
) => {
  const URL =
    constants.GLOBAL_SEARCH_BASE_URL + "v1/search/globalsearchrecevingdetails";
  return post(URL, paramObj, true, true, false);
};

export const postViewedStores = (payload: any) => {
  const URL =
    constants.DOT_NET_API_BASE_URL + "v1/sp/reports/updaterealtimestores";

  return post(URL, payload, true, true, false);
};

export const getViewedStores = (params: any) => {
  const queryString = getQueryString(params);
  const URL =
    constants.DOT_NET_API_BASE_URL +
    "v1/sp/reports/realtimestores?" +
    queryString;
  return get(URL, true, true, false);
};

export const postApproveOrRejectManualTransfer = (payload: any) => {
  const URL =
    constants.DOT_NET_API_BASE_URL +
    "v1/sp/receivings/manualtransferpo/approval";

  return post(URL, payload, true, true);
};

export const getGrossMarginInventoryCycle = (storeId: string) => {
  const URL =
    constants.DOT_NET_API_BASE_URL +
    `v1/sp/reports/grossmargin/inventorycycle?storeId=${storeId}`;
  return get(URL, true, true);
};

export const postGrossMarginFinalizeReport = (payload: any) => {
  const URL =
    constants.DOT_NET_API_BASE_URL + "v1/sp/reports/grossmargin/finalize";
  return post(URL, payload, true, true);
};

export const weeklyReceivingReport = (props: any) => {
  const URL =
    constants.DOT_NET_API_BASE_URL +
    `v1/sp/reports/weeklyreceiving?storeId=${props.storeId}&weekEndingDate=${props.weekEnding}&startingDate=${props.startingDate}`;
  return get(URL, true, true, false, props.signal);
};

export const getVendorList = ({
  DateFrom,
  DateTo,
  storeId,
  signal
}: {
  DateFrom: string;
  DateTo: string;
  storeId: string;
  signal?: AbortSignal | null;
}) => {
  const queryString = getQueryString({
    DateFrom,
    DateTo,
    storeId
  });
  const URL =
    constants.DOT_NET_API_BASE_URL +
    "v1/sp/reports/purchaseregister/vendor/list?" +
    queryString;
  return get(URL, true, true, false, signal);
};

export const getPurchaseReportsData = ({
  storeId,
  fromDate,
  toDate,
  sortParam,
  pageIndex,
  pageSize,
  vendorId,
  searchValue,
  sortOrder,
  signal
}: PurchaseReportsRequestParams) => {
  const URL = constants.DOT_NET_API_BASE_URL + "v1/sp/reports/purchaseregister";
  return post(
    URL,
    {
      storeId,
      DateFrom: fromDate,
      DateTo: toDate,
      sortParam,
      pageIndex,
      pageSize,
      vendorId,
      sortOrder,
      searchValue
    },
    true,
    true,
    false,
    signal
  );
};

export const physicalInventoryDate = (props: { storeId: string }) => {
  const queryString = getQueryString(props);
  const URL =
    constants.DOT_NET_API_BASE_URL +
    `v1/sp/receivings/PhysicalInventory?${queryString}`;
  return get(URL, true, true);
};

export const getSalesReportTableData = (props: {
  storeId: string;
  date: string;
}) => {
  const queryString = getQueryString(props);
  const URL =
    constants.DOT_NET_API_BASE_URL +
    `v1/sp/reports/salesinquiry?${queryString}`;
  return get(URL, true, true);
};

export const postReceivingListExport = (payload: any) => {
  const URL = constants.DOT_NET_API_BASE_URL + "v1/sp/receivings/list/export";

  return post(URL, payload, true, true);
};

export const getCheckEowFinalized = (props: {
  storeId: string;
  weekEndingDate: string;
}) => {
  const queryString = getQueryString(props);
  const URL =
    constants.DOT_NET_API_BASE_URL +
    `v1/sp/reports/weeklyreceiving/eow/isfinalized?${queryString}`;
  return get(URL, true, true);
};

export const eowDocumentStatus = (props: {
  storeId: string;
  startDate: string;
  endDate: string;
}) => {
  const queryString = getQueryString(props);
  const URL =
    constants.DOT_NET_API_BASE_URL +
    `v1/sp/reports/weeklyreceiving/eow/documentStatus?${queryString}`;
  return get(URL, true, true);
};

export const submitEowFinalize = (payload: {
  reportStartDate: string;
  reportEndDate: string;
  storeId: string;
  createdBy: string;
  response: string;
}) => {
  const URL =
    constants.DOT_NET_API_BASE_URL + "v1/sp/reports/weeklyreceiving/finalize";

  return put(URL, payload, true, true);
};

export const postNotificationPreference = (payload: object) => {
  const URL =
    constants.DOT_NET_API_BASE_URL + "v1/sp/Alert/updateNotificationPreference";
  return post(URL, payload, true, true);
};

export const getNotificationLog = ({
  storeId,
  startDate,
  endDate,
  type,
  status
}: {
  storeId: string;
  startDate: string;
  endDate: string;
  type: string[];
  status: string[];
}) => {
  const URL = constants.DOT_NET_API_BASE_URL + "v1/sp/Alert/log/list";

  const payload = {
    storeId,
    startDate,
    endDate,
    types: type,
    statuses: status
  };

  return post(URL, payload, true, true);
};

export const dismissAllAlerts = (payload: any) => {
  const URL = constants.DOT_NET_API_BASE_URL + "v1/sp/Alert";

  return post(URL, payload, true, true);
};

export const getProduceMovementRecord = ({
  storeId,
  endDate: weekEndingDate,
  departmentId: department,
  signal
}: {
  storeId: string;
  endDate: string;
  departmentId: string;
  signal?: AbortSignal;
}) => {
  const queryString = getQueryString({
    storeId,
    weekEndingDate,
    department
  });

  const URL =
    constants.DOT_NET_API_BASE_URL +
    `v1/sp/reports/productmovement?${queryString}`;

  return get(URL, true, true, false, signal);
};

export const postProduceMovementRecord = (payload: any) => {
  const URL =
    constants.DOT_NET_API_BASE_URL + "v1/sp/reports/productmovement/save";

  return post(URL, payload, true, true);
};

export const getItemOrderedData = (payload: {
  storeId: string;
  weekEndingDate: string;
  itemIds: string[];
  department: string;
}) => {
  const URL = constants.DOT_NET_API_BASE_URL + "v1/sp/reports/itemmovement";

  return post(URL, payload, true, true);
};

export const getPMRJITConfiguration = ({ storeId }: { storeId: string }) => {
  const queryString = getQueryString({ storeId });
  const URL =
    constants.DOT_NET_API_BASE_URL +
    `v1/sp/reports/productmovement/configurations?${queryString}`;

  return get(URL, true, true, false);
};

export const postPMRJITConfiguration = (payload: any) => {
  const URL =
    constants.DOT_NET_API_BASE_URL +
    "v1/sp/reports/productmovement/configurations";

  return post(URL, payload, true, true);
};

export const postProduceMovementSubmit = (payload: any) => {
  const URL =
    constants.DOT_NET_API_BASE_URL + "v1/sp/reports/productmovement/submit";

  return post(URL, payload, true, true);
};

export const postPMRJITLastAccessedDate = (payload: {
  storeId: string;
  department: string;
  weekEndingDate: string;
  lastAccessedDate: string;
}) => {
  const URL =
    constants.DOT_NET_API_BASE_URL +
    "v1/sp/reports/productmovement/accesseddate";

  return patch(URL, payload, true, true);
};
